import * as React from "react";
import PropTypes from "prop-types";
import Container from '@mui/material/Container';
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import FormControlLabel from "@mui/material/FormControlLabel";
import Collapse from '@material-ui/core/Collapse';
import Switch from "@mui/material/Switch";
import DeleteIcon from "@mui/icons-material/Delete";
import FilterListIcon from "@mui/icons-material/FilterList";
import SearchIcon from '@mui/icons-material/Search';
import { visuallyHidden } from "@mui/utils";
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import {Button, FormControl, OutlinedInput, TextField} from "@mui/material";
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import Grid from '@material-ui/core/Grid';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import WordsSVG from "../assets/media/Icon_Words.svg";
import SentencesSVG from "../assets/media/Icon_lines.svg";
import PageSVG from '../assets/media/Pages.svg';
import { useNavigate } from "react-router-dom";
import DonutChart from "./donutchart";
import TableDonutChart from "./tableDonutChart";
import {borderRadius} from "@mui/system";
import Select from "react-select";
import ClearIcon from '@mui/icons-material/Clear';
import get from 'lodash/get';
import { PROFANITY_OPTIONS, SENTIMENT_OPTIONS } from "../utilities/constants";
import SentimentLabel from "./SentimentLabel";
import DocumentStatusLabel from "./DocumentStatusLabel";
import ProfanityLabel from "./ProfanityLabel";
import Box from "./base/Box";


function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

// This method is created for cross-browser compatibility, if you don't
// need to support IE11, you can use Array.prototype.sort() directly
function stableSort(array, comparator) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) {
      return order;
    }
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

const headCells = [
  {
    id: "collapse",
    disablePadding: true,
    label: ""
  },
  {
    id: "name",
    numeric: false,
    disablePadding: true,
    label: "Document Name",
  },
  // Disabled Group
  // {
  //   id: "group",
  //   numeric: true,
  //   disablePadding: false,
  //   label: "Group",
  // },
  {
    id: "status",
    numeric: true,
    disablePadding: false,
    label: "Status",
  },
  {
    id: "profanities",
    numeric: true,
    disablePadding: false,
    label: "Profanities",
  },
  // {
  //   id: "score",
  //   numeric: true,
  //   disablePadding: false,
  //   label: "Sentiment Feedback",
  // }
  {
    id: 'label',
    numeric: true,
    disablePadding: false,
    label: 'Sentiment'
  }
];

function EnhancedTableHead(props) {
  const {
    onSelectAllClick,
    order,
    orderBy,
    numSelected,
    rowCount,
    onRequestSort,
  } = props;
  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead>
      <TableRow sx={{ backgroundColor: "#0065c91A" }}>
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            align={"center"}
            sortDirection={orderBy === headCell.id ? order : false}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : "asc"}
              onClick={createSortHandler(headCell.id)}
              hideSortIcon
            >
              {headCell.label}
              {/* {orderBy === headCell.id ? (
                <Box component="span" sx={visuallyHidden}>
                  {order === "desc" ? "sorted descending" : "sorted ascending"}
                </Box>
              ) : null} */}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

EnhancedTableHead.propTypes = {
  numSelected: PropTypes.number.isRequired,
  onRequestSort: PropTypes.func.isRequired,
  onSelectAllClick: PropTypes.func.isRequired,
  order: PropTypes.oneOf(["asc", "desc"]).isRequired,
  orderBy: PropTypes.string.isRequired,
  rowCount: PropTypes.number.isRequired,
};

function EnhancedTableToolbar(props) {
  const { numSelected, total, showFilters, setShowFilters } = props;

  return (
    <Toolbar>
      <Typography
        sx={{ flex: "1 1 100%", marginLeft: -1 }}
        variant="p"
        id="tableTitle"
        component="div"
      >
        {`Showing ${numSelected} of ${total} Documents`}
      </Typography>

      <Tooltip title="Filter list">
        <Button className="filterbtn" onClick={() => {
            localStorage.setItem('filterOpen', "close");
          setShowFilters(!showFilters)
        }}>
          <FilterListIcon className="caseAddIcn" /> Filters
        </Button>
      </Tooltip>
    </Toolbar>
  );
}

EnhancedTableToolbar.propTypes = {
  numSelected: PropTypes.number.isRequired,
};

const SingleRow = (props) => {
  const navigate = useNavigate();
  const [open, setOpen] = React.useState(false);
  const isItemSelected = props.isItemSelected;
  const row = props.data;

  return (
    <React.Fragment>
      <TableRow
        hover
        tabIndex={-1}
        key={row.id}
        selected={isItemSelected}
        sx={{
          '&.MuiTableRow-root:hover':{
            backgroundColor: '#0065c933',
            cursor: 'pointer'
          },
        }}
      >
        <TableCell>
          <IconButton aria-label="expand row" size="small" style={{ border: '1px solid #E2E2E2',borderRadius: '6px' }} onClick={() => setOpen(!open)}>
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>

        <TableCell 
          align="center" 
          onClick={() => {
            // navigate(`/document/overview/${row.file_id}`) 
            navigate(`/case/detail/${row.project_id}/document/${row.file_id}`);
          }}
        >
          <span>{row.file_name}</span>
        </TableCell>
        {/* Disabled Group */}
        {/* <TableCell align="center"
          onClick={() => {
            // navigate(`/document/overview/${row.file_id}`) 
            navigate(`/case/detail/${row.project_id}/document/${row.file_id}`);
          }}
        >Group 0</TableCell> */}
        <TableCell align="center"
          onClick={() => {
            // navigate(`/document/overview/${row.file_id}`) 
            navigate(`/case/detail/${row.project_id}/document/${row.file_id}`);
          }}
        >
          <DocumentStatusLabel status={row.status} sx={{margin: 'auto'}} />
        </TableCell>
        <TableCell align="center"
          key={row.id}
          onClick={() => {
            // navigate(`/document/overview/${row.file_id}`) 
            navigate(`/case/detail/${row.project_id}/document/${row.file_id}`);
          }}
        >
          <ProfanityLabel value={row.profanity} sx={{margin: 'auto'}} />
        </TableCell>
        {/* <TableCell align="center" style={{ width: 100 }} */}
        {/*   onClick={() => { */}
        {/*     // navigate(`/document/overview/${row.file_id}`) */} 
        {/*     navigate(`/case/detail/${row.project_id}/document/${row.file_id}`); */}
        {/*   }} */}
        {/* > */}
        {/*   {Math.sign(row.sentiment_score) == -1 ? ( */}
        {/*     <TableDonutChart */}
        {/*       data={[ */}
        {/*         (row.sentiment_score.toFixed(1) * 50 * -1) - 100, */}
        {/*         row.sentiment_score.toFixed(1) * 50 * -1, */}
        {/*       ]} */}
        {/*       colors={["#edc7d0", "#D12953"]} */}
        {/*       text={row.sentiment_score.toFixed(1)} */}
        {/*       textColor="#D12953" */}
        {/*     /> */}
        {/*   ) : ( */}
        {/*     <TableDonutChart */}
        {/*       data={[ */}
        {/*         (row.sentiment_score.toFixed(1) * 50) - 100, */}
        {/*         row.sentiment_score.toFixed(1) * 50 */}
        {/*       ]} */}
        {/*       colors={["#c9f3de", "#14804A"]} */}
        {/*       text={ row.sentiment_score.toFixed(1) == '0.0' ? */}
        {/*         ` ${row.sentiment_score.toFixed(1)}` */} 
        {/*         : */} 
        {/*         `+${row.sentiment_score.toFixed(1)}` */}
        {/*       } */}
        {/*       textColor="#14804A" */}
        {/*     /> */}
        {/*   )} */}
        {/* </TableCell> */}
        <TableCell align="center"
          onClick={() => {
            // navigate(`/document/overview/${row.file_id}`) 
            navigate(`/case/detail/${row.project_id}/document/${row.file_id}`);
          }}
        >
          <SentimentLabel sentiment={row.sentiment_label} sx={{margin: 'auto'}} />
        </TableCell>
      </TableRow>

      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Container style={{ display: 'flex', flexDirection: 'row', paddingTop: "20px", paddingBottom: "20px", paddingLeft: "80px", paddingRight: "80px", gap: "64px" }}>
              <div style={{ display: "flex", gap: "16px" }}>
                <div style={{ backgroundColor: "#E2E2E2", borderRadius: "100%", paddingLeft: "4px", paddingRight: "4px", width: "48px", display: 'flex', justifyContent: 'center'}}>
                  <img src={PageSVG} />
                </div> 

                <div>
                  <Typography style={{ fontSize: '16px', color: '#402E46', opacity: '0.7' }}>No. of Pages</Typography>
                  <Typography style={{ fontSize: "16px" }}>{row.total_pages}</Typography>
                </div>
              </div>

              {/* Disabled Paragraph */}
              {/* <div style={{ display: "flex", gap: "16px" }}>
                <div style={{ backgroundColor: "#E2E2E2", borderRadius: "100%", paddingLeft: "4px", paddingRight: "4px", paddingTop: "3px", width: "48px" }}>
                  <img src={SentencesSVG} />
                </div>

                <div>
                  <Typography style={{ fontSize: '16px', color: '#402E46', opacity: '0.7' }}>No. of Paragraphs</Typography>
                  <Typography style={{ fontSize: "16px" }}>{row.total_paragraphs}</Typography>
                </div>
              </div> */}

              <div style={{ display: "flex", gap: "16px" }}>
                <div style={{ backgroundColor: "#E2E2E2", borderRadius: "100%", paddingLeft: "4px", paddingRight: "4px", paddingTop: "3px", width: "48px" }}>
                  <img src={WordsSVG} />
                </div>

                <div>
                  <Typography style={{ fontSize: '16px', color: '#402E46', opacity: '0.7' }}>No. of Lines</Typography>
                  <Typography style={{ fontSize: "16px" }}>{row.total_lines}</Typography>
                </div>
              </div>

              {/* <div style={{ display: "flex", alignItems: "center", gap: "8px" }}> */}
              {/*   <div style={{ backgroundColor: "#E2E2E2", borderRadius: "100%", paddingLeft: "4px", paddingRight: "4px", paddingTop: "3px", width: "fit-content" }}> */}
              {/*     <LocalOfferOutlinedIcon/> */}
              {/*   </div> */}

              {/*   { row.tagList && row.tagList.length > 0 ? */}
              {/*     <div style={{ backgroundColor: "#ddeeff", paddingLeft: "10px", paddingRight: "10px", paddingTop: "5px", paddingBottom: "5px", fontSize: "16px", borderRadius: "8px", color: "#402E46" }}> */}
              {/*       { row.tagList } */} 
              {/*     </div> */}
              {/*   : */} 
              {/*     <div style={{ backgroundColor: "#ddeeff", paddingLeft: "10px", paddingRight: "10px", paddingTop: "5px", paddingBottom: "5px", fontSize: "16px", borderRadius: "8px", color: "#402E46" }}> */}
              {/*       No tags */}
              {/*     </div> */}
              {/*   } */}
              {/* </div> */}
            </Container>
          </Collapse>
        </TableCell>
      </TableRow>
    </React.Fragment>
  )
}

const Filters = ({ filters, setFilters, fetchCases, setPage }) => {
    const localStorageData = localStorage.getItem('filter');
  const [data, setData] = React.useState(localStorageData ? JSON.parse(localStorageData) : {});
  const documentNameDefault= get(JSON.parse(localStorageData), 'document_name');
  const [documentName, setDocumentName] = React.useState(documentNameDefault);
  return (
 <Box key={JSON.stringify(data)} sx={{ml: 3, mr: 4, mt: -2, mb: 4}}>
  <form className="filterForm">
    <Box sx={{'div[class$="placeholder"]': {
        fontFamily: '"Roboto","Helvetica","Arial","sans-serif"',
        fontWeight: 200
    },
        'div[class$="singleValue"]': {
        color: '#bebebe',
        fontFamily: '"Roboto","Helvetica","Arial","sans-serif"',
        fontWeight: 200
    },
        display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between'}}>
    <Box sx={{width: ['100%', '100%', 'calc((100% /3) - 1rem)']}}>
    <FormControl fullWidth>
      <p className="caseInpHead">Document Name</p>
      <OutlinedInput
          value={documentName || ''}
          id={"documentName"}
          onChange={(e) => {
              setDocumentName(e.target.value);
            // setData({...data, document_name: e.target.value})
          }}
          placeholder="Document Name"
          className="caseInp"
      />
    </FormControl>
    </Box>
    <Box sx={{width: ['100%', '100%', 'calc((100% /3) - 1rem)']}}>
    <FormControl fullWidth>
      <p className="caseInpHead">Status</p>
      <Select
          value={get(data, 'status') || {
              label: 'Select',
              value: null,
          }}
          options={[
            {
              label: 'Completed',
              value: 'predicted',
            },
            {
              label: 'Pending',
              value: 'uploaded,standardizationPending,standardized,predictionPending',
            },
            {
              label: 'Errored',
              value: 'errored',
            }]}
          className="caseInp"
          // isDisabled={processing}
          menuPosition={"fixed"}
          onChange={(e) => {
            setData({...data, status: e})
          }}
          placeholder={"Select"}
      />
    </FormControl>
    </Box>
    <Box sx={{width: ['100%', '100%', 'calc((100% /3) - 1rem)']}}>
    <FormControl fullWidth>
      <p className="caseInpHead">Profanity</p>
      <Select
          value={get(data, 'profanity') || {
              label: 'Select',
              value: null,
          }}
          options={PROFANITY_OPTIONS}
          className="caseInp"
          // isDisabled={processing}
          menuPosition={"fixed"}
          onChange={(e) => {
            setData({...data, profanity: e})
          }}
      />
    </FormControl>
    </Box>
    <Box sx={{width: ['100%', '100%', 'calc((100% /3) - 1rem)']}}>
    <FormControl fullWidth>
      <p className="caseInpHead">Sentiment</p>
      <Select
          value={get(data, 'sentiement_label') || {
              label: 'Select',
              value: null,
          }}
          options={SENTIMENT_OPTIONS}
          className="caseInp"
          // isDisabled={processing}
          menuPosition={"fixed"}
          onChange={(e) => {
            setData({...data, sentiement_label: e})
          }}
      />
    </FormControl>
    </Box>
      <Box sx={{ alignItems:'center', mt: 5, width: ['100%', '100%', 'calc((100% /3) - 1rem)'], display: 'flex', justifyContent: 'flex-end' }}>
        <Button sx={{height: '2.3rem'}} onClick={() => {
            const filterDataObject = {
                "document_name": documentName,
                "status": get(data, 'status.value', null),
                "profanity": get(data, 'profanity.value', null),
                "sentiement_label": get(data, 'sentiement_label.value', null),
            }
            localStorage.setItem('filter', JSON.stringify({ ...data, document_name: documentName}));
            localStorage.setItem('filterOpen', "open");
            // fetchCases(null, { filter:  filterDataObject})
            setFilters(filterDataObject)
            setPage(1)
        }} className="caseCancelBtn">
          <SearchIcon /> Search
        </Button>

        <Button type="reset" value={"reset"} sx={{height: '2.3rem', mr: '0 !important'}}
                onClick={()=> {
                    localStorage.removeItem('filter');
                    localStorage.setItem('filterOpen', "open");
                    document.getElementById("documentName").value = "";
                    setData({});
                    setFilters({});
                    setPage(1)
                }}
                className="caseCreateBtn">
          <ClearIcon /> Clear
        </Button>
      </Box>
    </Box>
  </form>
</Box>)}


export default function EnhancedTable(props) {
  const [order, setOrder] = React.useState("asc");
  const [orderBy, setOrderBy] = React.useState("calories");
  const [selected, setSelected] = React.useState([]);
  const [page, setPage] = React.useState(0);
  const [dense, setDense] = React.useState(false);
  const [rowsPerPage, setRowsPerPage] = React.useState(5);
  const filterOpen = false;
  const localStoragefilter = localStorage.getItem('filterOpen') === 'open' ? true : false;
  const [showFilters, setShowFilters] = React.useState(localStoragefilter);
  const rows = [...props.rowData];
  const { fetchCases, filters, setFilters, totalItems, setPage: resetPage, pageSize } = props;
  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelected = rows.map((n) => n.name);
      setSelected(newSelected);
      return;
    }
    setSelected([]);
  };

  const handleClick = (event, name) => {
    const selectedIndex = selected.indexOf(name);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, name);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }

    setSelected(newSelected);
  };

  const handleChangePage = (event, newPage) => {
    // setPage(newPage);
    props.getPage(newPage + 1);
  };

  const handleChangeRowsPerPage = (event) => {
    // setRowsPerPage(parseInt(event.target.value, 10));
    props.setPageSize(event.target.value);
    // setPage(0);
  };

  const isSelected = (name) => selected.indexOf(name) !== -1;

  // Avoid a layout jump when reaching the last page with empty rows.
  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - rows.length) : 0;

  return (
    <Box sx={{ width: "100%" }}>
      <Paper sx={{ width: "100%", mb: 2, boxShadow: "0px 5px 15px #00000029", borderRadius: "14px" }}>
        <EnhancedTableToolbar numSelected={rows.length } total={totalItems} showFilters={showFilters} setShowFilters={setShowFilters} setPage={props.getPage}/>
        {showFilters && <Filters filters={filters} setFilters={setFilters} fetchCases={fetchCases} setPage={resetPage}/>}
          {rows && rows.length > 0 ? <TableContainer>
              <Table aria-label="collapsible table">
                  <EnhancedTableHead
                      numSelected={selected.length}
                      order={order}
                      orderBy={orderBy}
                      onSelectAllClick={handleSelectAllClick}
                      onRequestSort={handleRequestSort}
                      rowCount={rows.length}
                  />
                  <TableBody>
                      {/* if you don't need to support IE11, you can replace the `stableSort` call with:
                 rows.sort(getComparator(order, orderBy)).slice() */}
                      {stableSort(rows, getComparator(order, orderBy))
                          .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                          .map((row, index) => {
                              const isItemSelected = isSelected(row.name);
                              const labelId = `enhanced-table-checkbox-${index}`;

                              return (
                                  <SingleRow isItemSelected={isItemSelected} data={row}/>
                              );
                          })}
                  </TableBody>
              </Table>
              <TablePagination
                  rowsPerPageOptions={[]}
                  component="div"
                  count={props.totalItems}
                  rowsPerPage={pageSize}
                  page={props.currentPage - 1}
                  onPageChange={handleChangePage}
                  labelDisplayedRows={(data) => {
                      return `${data.from}-${data.to} of ${data.count}`;
                  }}
                  nextIconButtonProps={
                      props.totalPages > props.currentPage ?
                          undefined
                          :
                          {
                              disabled: true
                          }
                  }
              />
          </TableContainer> : <Grid>
              <p
                  style={{
                      textAlign: "center",
                      marginTop: "30px",
                      lineHeight: 2,
                      fontWeight: "100",
                      paddingBottom: '30px'
                  }}
              >
                  It seems there is nothing here!
              </p>
          </Grid>}

      </Paper>
    </Box>
  )
}
